import { startCase } from 'lodash';

import config from 'js/app/config';
import path from 'js/lib/path';

import type { MergedDegreeDescriptionPageFields } from 'bundles/degree-description/contexts/DegreeDescriptionPageContext';
import type { BreadcrumbSchemaMarkup } from 'bundles/seo/utils/schemaMarkupUtils';
import { getSchemaBreadcrumbsList } from 'bundles/seo/utils/schemaMarkupUtils';

type PageInfoType = {
  pageName: string;
  pathName: string;
};

/**
 * These constants are used for our structured data markup, separately from our navigation items
 */
export const PAGE_NAMES_MARKUP = Object.freeze({
  overview: 'Overview',
  admissions: 'Admissions',
  academics: 'Academics',
  careers: 'Careers',
  tuitionFinancing: 'Tuition and Financing',
  studentExperience: 'Student Experience',
});

export const getProgramCategoryName = (programCategoryId?: string | null) => {
  if (!programCategoryId) return undefined;
  const categoryName = startCase(programCategoryId);
  return `${categoryName} Degrees`;
};

export const getProviderMarkupData = (pageContent: MergedDegreeDescriptionPageFields) => {
  return {
    '@type': 'CollegeOrUniversity',
    '@id': `${config.url.base}${pageContent?.partner?.partnerSlug}#institution`,
    name: pageContent?.partner?.partnerName,
  };
};

export const getBreadcrumbListMarkupData = (
  pageContent: MergedDegreeDescriptionPageFields,
  pageInfo: PageInfoType
): BreadcrumbSchemaMarkup => {
  return getSchemaBreadcrumbsList([
    {
      name: 'Online Degrees',
      absoluteUrl: `${config.url.base}degrees`,
    },
    {
      name: getProgramCategoryName(pageContent.programCategoryId) ?? '',
      absoluteUrl: pageContent.programCategoryId
        ? `${config.url.base}degrees/${pageContent.programCategoryId}`
        : `${config.url.base}degrees`,
    },
    {
      name: pageContent.programName ?? '',
      absoluteUrl: pageContent.slug ? `${config.url.base}degrees/${pageContent.slug}` : `${config.url.base}degrees`,
    },
    {
      name: pageInfo.pageName,
      absoluteUrl: path.join(config.url.base, pageInfo.pathName),
    },
  ]);
};

export const getCredentialCategoryMarkupData = () => {
  return {
    '@type': 'DefinedTerm',
    '@id': 'https://credreg.net/ctdl/terms/#MasterDegree',
    name: "Master's Degree",
    inDefinedTermSet: {
      '@type': 'DefinedTermSet',
      '@id': 'https://credreg.net/ctdl/terms/',
      name: 'Credential Transparency Description Language',
    },
  };
};
